/* @flow */

import {
  getConversationTopic,
  getConversationParticipants,
  getConversationMessages,
} from '@braindate/domain/lib/conversation/util';
import {
  getTopicAuthor,
  getTopicTitle,
  isUserTopicAuthor,
  isGroupTopic,
  isFishbowlTopic,
} from '@braindate/domain/lib/topic/util';
import {
  getUserId,
  getUserFirstName,
  getUserFullName,
  getUserAvatar,
} from '@braindate/domain/lib/user/util';
import {
  assertObject,
  assertArray,
  assertNumber,
} from '@braindate/util/lib/assert';

import { isMessageRead, getMessageId } from './messageUtils';

import type {
  Conversation,
  ConversationMessage,
} from '@braindate/domain/lib/conversation/type';
import type { Picture } from '@braindate/domain/lib/base/type';
import type { User } from '@braindate/domain/lib/user/type';

/*
|------------------------------------------------------------------------------
| GETTERS
|------------------------------------------------------------------------------
*/

/**
 * Get the participants of the conversation, sorted by the topic author first
 * and the other participants alphabetically by first names.
 * @param  {Conversation} conversation - Conversation to get the participants from
 * @return {Array<User>} Sorted participants by host first then by firstnames
 *
 * @throws Will throw an exception if parameter `conversation` is not an object
 */
export function getConversationParticipantsByTopicAuthorFirst(
  conversation: Conversation,
): Array<User> {
  assertObject(conversation, 'conversation');

  const authorId = getConversationTopicAuthorId(conversation);
  const participants = getConversationParticipants(conversation);
  let host;

  for (let i = 0; i < participants.length; i += 1) {
    if (getUserId(participants[i]) === authorId) {
      // Remove the host from the list as the host will always be the first in the list
      [host] = participants.splice(i, 1);
      break;
    }
  }

  // Sort all participants but the host
  const sortedParticipants = sortParticipantsByFirstName(participants);

  if (host) {
    sortedParticipants.unshift(host);
  } else if (sortedParticipants.length) {
    throw new Error('No host was found in conversation');
  }

  return sortedParticipants;
}

/**
 * Sprt participants by their first names
 * @param  {Array<User>} participants - Participants
 * @return {Array<User>} Sorted participants by firstname
 *
 * @throws Will throw an exception if parameter `participants` is not an array
 */
export function sortParticipantsByFirstName(
  participants: Array<User>,
): Array<User> {
  assertArray(participants, 'participants');

  return participants.sort((user, user2) =>
    getUserFirstName(user).localeCompare(getUserFirstName(user2)),
  );
}

export function getConversationReadMessages(
  conversation: Conversation,
): Array<ConversationMessage> {
  assertObject(conversation, 'conversation');

  return (getConversationMessages(conversation) || []).filter((message) =>
    isMessageRead(message, conversation),
  );
}

export function getConversationLastReadMessage(
  conversation: Conversation,
): ?ConversationMessage {
  assertObject(conversation, 'conversation');

  const messages = (getConversationMessages(conversation) || []).filter(
    (message) => isMessageRead(message, conversation),
  );

  const { length: messageCount } = messages;

  return messageCount ? messages[messageCount - 1] : undefined;
}

// should only be used if messages in conversation
export function getConversationReadMessagesByLastReadMessage(
  conversation: Conversation,
  lastReadMessageId: number,
): Array<ConversationMessage> {
  assertObject(conversation, 'conversation');
  assertNumber(lastReadMessageId, 'lastReadMessageId');

  return (getConversationMessages(conversation) || []).filter(
    (message) => getMessageId(message) <= lastReadMessageId,
  );
}

export function isConversationRead(
  conversation: Conversation,
  lastReadMessageId: number,
): ?boolean {
  assertObject(conversation, 'conversation');
  assertNumber(lastReadMessageId, 'lastReadMessageId');

  // FIXME: create util for `"messages" in conversation`
  if (conversation && 'messages' in conversation && lastReadMessageId) {
    const messages = getConversationMessages(conversation) || [];
    const { length: messageCount } = messages;

    if (messageCount > 0) {
      const lastMessage = messages[messageCount - 1];

      return getMessageId(lastMessage) === lastReadMessageId;
    }

    return true;
  }
}

/*
|------------------------------------------------------------------------------
| TOPIC GETTERS
|------------------------------------------------------------------------------
*/

/**
 * Get the title of the conversation topic
 * @param  {Conversation} conversation - Conversation to get the title from
 * @param  {string} [locale] - Locale in which to get the title
 * @return {string} Title
 */
export function getConversationTopicTitle(
  conversation: Conversation,
  locale?: string,
): string {
  return getTopicTitle(getConversationTopic(conversation), locale);
}

/**
 * Get the author of the conversation topic
 * @param  {Conversation} conversation - Conversation to get the author from
 * @return {User} Author
 */
export function getConversationTopicAuthor(conversation: Conversation): User {
  return getTopicAuthor(getConversationTopic(conversation));
}

/**
 * Get the id of the conversation topic author
 * @param  {Conversation} conversation - Conversation to get the id from
 * @return {number} Id
 */
export function getConversationTopicAuthorId(
  conversation: Conversation,
): number {
  return getUserId(getTopicAuthor(getConversationTopic(conversation)));
}

export function isUserConversationHost(
  conversation: Conversation,
  user: User,
): boolean {
  assertObject(conversation, 'conversation');
  assertObject(user, 'user');

  return getConversationTopicAuthorId(conversation) === getUserId(user);
}

export function isUserConversationGuest(
  conversation: Conversation,
  user: User,
): boolean {
  return !isUserConversationHost(conversation, user);
}

/*
|------------------------------------------------------------------------------
| FILTERS
|------------------------------------------------------------------------------
*/

export function getConversationOtherParticipants(
  conversation: Conversation,
  user: User,
): Array<User> {
  const participants = getConversationParticipants(conversation);

  // $FlowIssue
  return participants.filter((p) => getUserId(p) !== getUserId(user));
}

export function getConversationOtherParticipant(
  conversation: Conversation,
  user: User,
): User {
  const participants = getConversationParticipants(conversation);
  const others = participants.filter((p) => getUserId(p) !== getUserId(user));

  return others[0];
}

/*
|------------------------------------------------------------------------------
| PARTICIPANTS MAPPINGS
|------------------------------------------------------------------------------
*/

/**
 * Get the first names of the conversation participants
 * @param  {Conversation} conversation - Conversation to get the first names from
 * @param  {boolean} [byTopicAuthorFirst] - If true, sort by topic author first
 * @return {Array<string>} First names
 */
export function getConversationParticipantsFirstNames(
  conversation: Conversation,
  byTopicAuthorFirst?: boolean = false,
): Array<string> {
  let participants;

  if (byTopicAuthorFirst) {
    participants = getConversationParticipantsByTopicAuthorFirst(conversation);
  } else {
    participants = getConversationParticipants(conversation);
  }

  return participants.map((user) => getUserFirstName(user));
}

/**
 * Get the first names of the conversation participants, as a string
 * @param  {Conversation} conversation - Conversation to get the first names from
 * @param  {boolean} [byTopicAuthorFirst] - If true, sort by topic author first
 * @param  {string} [separator] - Separator between names
 * @return {string} First names
 */
export function getConversationParticipantsFirstNamesAsString(
  conversation: Conversation,
  byTopicAuthorFirst?: boolean = false,
  separator?: string = ', ',
): string {
  return getConversationParticipantsFirstNames(
    conversation,
    byTopicAuthorFirst,
  ).join(separator);
}

/**
 * Get the avatars of the conversation participants
 * @param  {Conversation} conversation - Conversation to get the avatars from
 * @param  {boolean} byTopicAuthorFirst - If true, sort by topic author first
 * @return {Array<Picture>} Avatars
 */
export function getConversationParticipantsAvatars(
  conversation: Conversation,
  byTopicAuthorFirst: boolean = false,
): Array<Picture> {
  let participants;

  if (byTopicAuthorFirst) {
    participants = getConversationParticipantsByTopicAuthorFirst(conversation);
  } else {
    participants = getConversationParticipants(conversation);
  }

  return participants.map((user) => getUserAvatar(user) || {});
}

/*
|------------------------------------------------------------------------------
| OTHER PARTICIPANTS MAPPINGS
|------------------------------------------------------------------------------
*/

export function getConversationOtherFirstNames(
  conversation: Conversation,
  user: User,
): Array<string> {
  return getConversationOtherParticipants(conversation, user).map((user) =>
    getUserFirstName(user),
  );
}

/**
 * Get the full names of the conversation other participants
 * @param  {Conversation} conversation - Conversation to get the full names from
 * @param  {User} user - Authenticated user
 * @return {Array<string>} Full names
 */
export function getConversationOtherFullNames(
  conversation: Conversation,
  user: User,
): Array<string> {
  return getConversationOtherParticipants(conversation, user).map((user) =>
    getUserFullName(user),
  );
}

/*
|------------------------------------------------------------------------------
| CHECKERS
|------------------------------------------------------------------------------
*/

/**
 * Check if the conversation topic author is the connected user
 * @param  {Conversation} conversation - Conversation to check
 * @param  {User} user - Authenticated user
 * @return {boolean} True if the conversation topic author is
 */
export function isConversationAboutOwnTopic(
  conversation: Conversation,
  user: User,
): boolean {
  return isUserTopicAuthor(user, getConversationTopic(conversation));
}

/**
 * Check if the conversation topic is a group braindate
 * @param  {Conversation} conversation - Conversation to check
 * @return {boolean} True if the conversation topic is
 */
export function isConversationAboutGroupBraindate(
  conversation: Conversation,
): boolean {
  return isGroupTopic(getConversationTopic(conversation));
}

/**
 * Check if the conversation topic is a fishbowl
 * @param  {Conversation} conversation - Conversation to check
 * @return {boolean} True if the conversation topic is
 */
export function isConversationAboutFishbowl(
  conversation: Conversation,
): boolean {
  return isFishbowlTopic(getConversationTopic(conversation));
}

export function hasConversationMessages(conversation: Conversation): boolean {
  // TODO: assert conversation

  return 'messages' in conversation;
}

export function hasConversationActions(conversation: Conversation): boolean {
  // TODO: assert conversation

  return 'actions' in conversation;
}
